<template>
  <aside>
    <v-navigation-drawer 
      :clipped="true"
      :mini-variant="mini"
      app
      :width="256"
      :mini-variant-width="68"
      color="secondary"
      class="stripe-sidenav">
      
      <div class="sidenav-content">
        <div class="sidenav-header">
          <v-btn
            icon
            @click="toggleMini"
            color="secondary"
            class="toggle-button"
          >
            <v-icon size="22px" class="t-bw-secondary--text">{{ mini ? 'mdi-menu-close' : 'mdi-menu-open' }}</v-icon>
          </v-btn>
        </div>

        <div class="sidenav-menu-container">
          <div v-if="mini" class="mini-nav-container">
            <div 
              v-for="(item, key) in filteredTopLevelItems"
              :key="`mini-menu-${key}`"
              class="mini-nav-item-wrapper"
            >
              <v-menu
                v-if="item.subMenu"
                :close-on-content-click="false"
                offset-x
                right
                :nudge-right="20"
                transition="slide-x-transition"
                class="mini-submenu"
                :open-delay="100"
                :close-delay="100"
              >
                <template v-slot:activator="{ on, attrs }">
                  <div 
                    class="mini-nav-item"
                    :class="{'mini-nav-item--active': isItemOrChildActive(item)}"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon size="20" class="t-bw-secondary--text">{{ item.icon }}</v-icon>
                  </div>
                </template>
                
                <v-card class="mini-submenu-card" color="secondary">
                  <v-list dense class="mini-submenu-list">
                    <v-list-item class="mini-submenu-header">
                      <v-list-item-avatar size="36" class="mr-3">
                        <v-icon class="t-bw-secondary--text">{{ item.icon }}</v-icon>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title class="t-bw-secondary--text font-weight-medium">
                          {{ getItemTranslatedName(item) }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    
                    <template v-for="(subItem, subKey) in item.subMenu">
                      <v-list-item
                        :key="`submenu-item-${subKey}`"
                        @click="navigateToItem(subItem)"
                        :class="{'mini-submenu-item--active': isItemActive(subItem)}"
                        class="mini-submenu-item"
                      >
                        <v-list-item-avatar size="28" class="mr-3">
                          <v-icon size="18" class="t-bw-secondary--text">{{ subItem.icon }}</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title class="t-bw-secondary--text">
                            {{ getItemTranslatedName(subItem) }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      
                      <!-- Si hay un tercer nivel anidado -->
                      <template v-if="subItem.subMenu">
                        <v-list-item
                          v-for="(nestedItem, nestedKey) in subItem.subMenu"
                          :key="`nested-item-${nestedKey}`"
                          @click="navigateToItem(nestedItem)"
                          :class="{
                            'mini-submenu-nested-item': true,
                            'mini-submenu-item--active': isItemActive(nestedItem)
                          }"
                        >
                          <v-list-item-avatar size="24" class="mr-3 ml-4">
                            <v-icon size="16" class="t-bw-secondary--text">{{ nestedItem.icon }}</v-icon>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title class="t-bw-secondary--text text-caption">
                              {{ getItemTranslatedName(nestedItem) }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                    </template>
                  </v-list>
                </v-card>
              </v-menu>
              
              <v-tooltip right v-else :disabled="!mini">
                <template v-slot:activator="{ on, attrs }">
                  <div 
                    class="mini-nav-item"
                    :class="{'mini-nav-item--active': isItemOrChildActive(item)}"
                    v-bind="attrs"
                    v-on="on"
                    @click="handleMiniItemClick(item)"
                  >
                    <v-icon size="20" class="t-bw-secondary--text">{{ item.icon }}</v-icon>
                  </div>
                </template>
                <span>{{ getItemTranslatedName(item) }}</span>
              </v-tooltip>
            </div>
          </div>
          
          <div v-else class="full-nav-container">
            <navigation-item
              v-for="(item, key) in filteredNavigationRules"
              :key="`menu-${key}`"
              :item="item"
              :menu-key="key"
              :active-route="$route.name"
            />
          </div>
        </div>
      </div>

      <template v-slot:append>
        <div class="sidenav-footer">
          <code v-if="!mini" class="t-bw-secondary--text">powered by <strong>AgTrace</strong></code>
        </div>
      </template>
    </v-navigation-drawer>

    <filters-menu :id="$router.currentRoute.name" :key="$route.fullPath" />
  </aside>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import NavigationItem from '@/module/configuration/navigation/components/NavigationItem.vue'
import NavigationService from '@/module/configuration/navigation/services/NavigationService'
import FiltersMenu from '@/components/FiltersMenu.vue'
import PermissionService from '@/services/PermissionService'

export default {
  name: 'LayoutSidenav',
  
  components: { 
    NavigationItem,
    FiltersMenu 
  },

  data() {
    return {
      profile: JSON.parse(localStorage.getItem('profile') || '{}')
    }
  },

  computed: {
    ...mapState('navigation', [
      'mini',
      'navigationRules',
      'customMenuItems',
      'disabledMenuItems'
    ]),

    filteredTopLevelItems() {
      // Solo retornar los elementos de primer nivel para el mini drawer
      return Object.values(this.filteredNavigationRules);
    },

    flattenedMenuItems() {
      const items = []
      const rules = {
        ...this.navigationRules,
        ...this.customMenuItems
      }

      const flatten = (menuItem) => {
        if (!menuItem.subMenu) {
          items.push(menuItem)
          return
        }

        Object.values(menuItem.subMenu).forEach(subItem => {
          if (subItem.subMenu) {
            Object.values(subItem.subMenu).forEach(nestedItem => {
              items.push(nestedItem)
            })
          } else {
            items.push(subItem)
          }
        })
      }

      Object.values(rules).forEach(flatten)
      return items.filter(item => !this.disabledMenuItems.includes(item.name))
    },

    filteredNavigationRules() {
      const rules = {
        ...this.navigationRules,
        ...this.customMenuItems
      }

      // Nueva función recursiva para filtrar y propagar permisos
      const filterMenu = (item, key, parentKey = '') => {
        const fullKey = parentKey ? `${parentKey}-${key}` : key;

        // Si está deshabilitado, retornar null
        if (this.disabledMenuItems.includes(fullKey)) return null;

        // Si tiene submenú, filtrar hijos primero
        let filteredSubMenu = null;
        if (item.subMenu) {
          filteredSubMenu = {};
          Object.entries(item.subMenu).forEach(([subKey, subItem]) => {
            const allowedSub = filterMenu(subItem, subKey, fullKey);
            if (allowedSub) filteredSubMenu[subKey] = allowedSub;
          });
        }

        // Verificar permisos del ítem actual
        const hasPermission = this.checkPermissions(item.permissionRequired);
        // Si no tiene permisos pero algún hijo sí, mostrar el padre igual
        if (!hasPermission && (!filteredSubMenu || Object.keys(filteredSubMenu).length === 0)) {
          return null;
        }

        // Si tiene hijos permitidos, agregarlos
        const processedItem = { ...item };
        if (filteredSubMenu && Object.keys(filteredSubMenu).length > 0) {
          processedItem.subMenu = filteredSubMenu;
        } else {
          delete processedItem.subMenu;
        }
        return processedItem;
      };

      const filtered = {};
      Object.entries(rules).forEach(([key, item]) => {
        const allowed = filterMenu(item, key);
        if (allowed) filtered[key] = allowed;
      });

      return filtered;
    }
  },

  methods: {
    ...mapActions('navigation', ['setMini', 'toggleMini']),
    
    getItemTo(item) {
      if (item.name === 'integrations') {
        return { 
          path: `/integrations/${(this.profile.company_owner || 'all').toLowerCase()}`
        }
      }
      return item.name ? { name: item.name } : {}
    },

    checkPermissions(permissions) {
      return PermissionService.check(permissions);
    },
    
    isItemOrChildActive(item) {
      if (!item) return false;
      
      // Comprobar si el item corresponde a la ruta actual
      if (item.name === this.$route.name) return true;
      
      // Comprobar si algún submenú está activo
      if (item.subMenu) {
        return Object.values(item.subMenu).some(subItem => 
          this.isItemOrChildActive(subItem)
        );
      }
      
      return false;
    },
    
    handleMiniItemClick(item) {
      if (item.subMenu) {
        // Si tiene submenú, expandir la navegación
        this.setMini(false);
      } else {
        // Si no tiene submenú, navegar directamente
        const route = this.getItemTo(item);
        if (route.path || route.name) {
          this.$router.push(route);
        }
      }
    },

    isItemActive(item) {
      if (!item) return false;
      return item.name === this.$route.name || 
             item.path === this.$route.path ||
             item.name === this.$route.meta?.parent;
    },
    
    navigateToItem(item) {
      if (!item) return;
      
      const route = this.getItemTo(item);
      if (route.path || route.name) {
        this.$router.push(route);
      }
    },
    
    getItemTranslatedName(item) {
      if (!item) return '';
      
      if (item.translationKey) {
        return this.$t(item.translationKey);
      }
      
      if (item.translations) {
        const currentLang = this.$i18n.locale;
        return item.translations[currentLang] || item.translations.ENGLISH || item.name;
      }
      
      return item.name || '';
    }
  },

  created() {
    this.$store.dispatch('navigation/initNavigationRules')
  },

  mounted() {
    this.$root.$on('update:company', (profile) => {
      this.profile = profile
    })
  },

  beforeDestroy() {
    this.$root.$off('update:company')
  }
}
</script>

<style lang="scss" scoped>
.stripe-sidenav {
  height: 100vh;
  top: 64px;
  max-height: calc(100% - 64px);
  transform: translateX(0%);
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-right: none !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  overflow: hidden;

  &:deep(.v-navigation-drawer__border) {
    display: none;
  }
}

.sidenav-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.sidenav-header {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.toggle-button {
  opacity: 0.8;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.1);
  }
}

.sidenav-menu-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  
  /* Estilo del scrollbar para navegadores webkit (Chrome, Safari) */
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
}

.sidenav-footer {
  height: 48px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  opacity: 0.7;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

/* Estilos para la navegación en modo mini */
.mini-nav-container {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
}

.mini-nav-item-wrapper {
  padding: 4px 0;
  display: flex;
  justify-content: center;
}

.mini-nav-item {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: var(--v-t-bw-secondary-base);
  opacity: 0.7;
  transition: all 0.2s ease;
  cursor: pointer;
  
  .v-icon {
    color: var(--v-t-bw-secondary-base) !important; /* Forzar color del icono */
  }
  
  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.08);
  }
  
  &--active {
    opacity: 1;
    background: rgba(255, 255, 255, 0.15);
  }
}

/* Estilos para el menú flotante de subitems */
.mini-submenu-card {
  overflow: visible;
  max-height: 85vh;
  overflow-y: auto;
  min-width: 220px;
  border-radius: 0 .3em .3em 0 !important; /* Solo radio en lado derecho */
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  margin-left: -5px;
  border: none; /* Eliminar todos los bordes */
  
  /* Estilos del scrollbar para el submenú */  
  &::-webkit-scrollbar {
    width: 5px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 10px;
  }
}

.mini-submenu-list {
  padding: 0;
  background-color: transparent !important;
}

/* Eliminar estilos del divider que ya no está */
.mini-submenu-divider {
  display: none;
}

.mini-submenu-header {
  padding: 16px 16px 8px 16px;
  background-color: rgba(255, 255, 255, 0.05);
  margin-bottom: 4px; /* Agregar un poco de espacio en lugar del divider */
  
  .v-list-item__avatar {
    margin-right: 16px;
  }
}

.mini-submenu-item {
  transition: all 0.2s ease;
  height: 40px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.08);
  }
}

.mini-submenu-item--active {
  background-color: rgba(255, 255, 255, 0.15);
  
  .v-icon {
    opacity: 1;
  }
}

.mini-submenu-nested-item {
  padding-left: 12px;
  height: 36px;
  opacity: 0.9;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.08);
    opacity: 1;
  }
  
  .v-list-item__title {
    font-size: 13px;
  }
}

/* Estilos para la navegación completa */
.full-nav-container {
  padding: 8px 0;
}

/* Sobreescribir estilos de Vuetify */
:deep(.v-list) {
  padding: 0;
  background: transparent !important;
}

:deep(.v-list-item) {
  min-height: 40px;
  padding: 0 16px;
}

:deep(.v-navigation-drawer__append) {
  margin-bottom: 0;
}
</style>
